import { EmployeeDocument, PayingClient } from '../models/models';
import { CacheErrorType, CacheErrorLogger } from '../utils/cacheErrorHandler';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiry: number;
  lastAccessed: number;
  size: number;
}

class CacheService {
  private static instance: CacheService;
  private cache: Map<string, CacheEntry<any>> = new Map();
  private cleanupInterval?: NodeJS.Timeout;
  private currentCacheSize: number = 0;
  private hitCount: number = 0;
  private missCount: number = 0;
  private evictionCount: number = 0;

  // Cache configuration
  private static readonly CACHE_CONFIG = {
    MAX_SIZE: 50 * 1024 * 1024, // 50MB max cache size
    MAX_ENTRIES: 1000, // Maximum number of cache entries
    CLEANUP_INTERVAL: 60 * 1000, // Run cleanup every minute
    MIN_CLEANUP_INTERVAL: 10 * 1000 // Minimum 10 seconds between cleanups
  };

  // Cache durations in milliseconds
  private static readonly CACHE_DURATIONS = {
    EMPLOYEES: 5 * 60 * 1000, // 5 minutes for employee data
    CUSTOMERS: 10 * 60 * 1000, // 10 minutes for customer data
    DEFAULT: 5 * 60 * 1000 // 5 minutes default
  };

  private constructor() {
    this.startAutomaticCleanup();
  }

  static getInstance(): CacheService {
    if (!CacheService.instance) {
      CacheService.instance = new CacheService();
    }
    return CacheService.instance;
  }

  /**
   * Start automatic cleanup interval
   */
  private startAutomaticCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, CacheService.CACHE_CONFIG.CLEANUP_INTERVAL);
  }

  /**
   * Stop automatic cleanup (useful for testing)
   */
  stopAutomaticCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = undefined;
    }
  }

  /**
   * Calculate approximate size of data in bytes
   */
  private calculateSize(data: any): number {
    try {
      const str = JSON.stringify(data);
      return new Blob([str]).size;
    } catch (error) {
      // Log serialization issues
      CacheErrorLogger.log({
        type: CacheErrorType.SERIALIZATION_ERROR,
        name: 'CacheError',
        message: `Failed to calculate size for data: ${error}`,
        key: undefined,
        originalError: error as Error
      });
      
      // If we can't stringify, estimate based on type
      if (data instanceof ArrayBuffer) return data.byteLength;
      if (typeof data === 'string') return data.length * 2; // UTF-16
      return 1024; // Default 1KB for unknown types
    }
  }

  /**
   * Evict least recently used entries to make space
   */
  private evictLRU(requiredSpace: number): void {
    const entries = Array.from(this.cache.entries());
    entries.sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);
    
    let freedSpace = 0;
    for (const [key, entry] of entries) {
      if (freedSpace >= requiredSpace) break;
      
      freedSpace += entry.size;
      this.currentCacheSize -= entry.size;
      this.cache.delete(key);
      this.evictionCount++;
      
      console.log(`[Cache] Evicted ${key} to free ${entry.size} bytes`);
    }
  }

  /**
   * Check if adding new entry would exceed limits
   */
  private ensureCapacity(newSize: number): void {
    // Check entry count limit
    if (this.cache.size >= CacheService.CACHE_CONFIG.MAX_ENTRIES) {
      this.evictLRU(newSize);
    }
    
    // Check size limit
    if (this.currentCacheSize + newSize > CacheService.CACHE_CONFIG.MAX_SIZE) {
      const requiredSpace = (this.currentCacheSize + newSize) - CacheService.CACHE_CONFIG.MAX_SIZE;
      this.evictLRU(requiredSpace);
    }
  }

  /**
   * Set data in cache with automatic expiry
   */
  set<T>(key: string, data: T, duration?: number): void {
    try {
      const now = Date.now();
      const expiry = now + (duration || CacheService.CACHE_DURATIONS.DEFAULT);
      const size = this.calculateSize(data);
      
      // Remove old entry if exists
      const oldEntry = this.cache.get(key);
      if (oldEntry) {
        this.currentCacheSize -= oldEntry.size;
      }
      
      // Ensure we have capacity
      this.ensureCapacity(size);
      
      this.cache.set(key, {
        data,
        timestamp: now,
        expiry,
        lastAccessed: now,
        size
      });
      
      this.currentCacheSize += size;
    } catch (error: any) {
      CacheErrorLogger.log({
        type: CacheErrorType.STORAGE_FULL,
        name: 'CacheError',
        message: `Failed to set cache key ${key}: ${error.message}`,
        key,
        originalError: error
      });
      // Don't throw - cache failures shouldn't break the app
    }
  }

  /**
   * Get data from cache, returns null if expired or not found
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      this.missCount++;
      return null;
    }

    const now = Date.now();
    
    // Check if expired
    if (now > entry.expiry) {
      this.currentCacheSize -= entry.size;
      this.cache.delete(key);
      this.missCount++;
      return null;
    }

    // Update last accessed time
    entry.lastAccessed = now;
    this.hitCount++;
    
    return entry.data as T;
  }

  /**
   * Check if a key exists and is not expired
   */
  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) {
      return false;
    }

    const now = Date.now();
    if (now > entry.expiry) {
      this.currentCacheSize -= entry.size;
      this.cache.delete(key);
      return false;
    }

    // Update last accessed time
    entry.lastAccessed = now;
    return true;
  }

  /**
   * Remove specific key from cache
   */
  delete(key: string): void {
    const entry = this.cache.get(key);
    if (entry) {
      this.currentCacheSize -= entry.size;
      this.cache.delete(key);
    }
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
    this.currentCacheSize = 0;
    this.hitCount = 0;
    this.missCount = 0;
    this.evictionCount = 0;
  }

  /**
   * Remove expired entries
   */
  cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];
    let freedSize = 0;
    
    this.cache.forEach((entry, key) => {
      if (now > entry.expiry) {
        keysToDelete.push(key);
        freedSize += entry.size;
      }
    });
    
    keysToDelete.forEach(key => {
      this.cache.delete(key);
    });
    
    this.currentCacheSize -= freedSize;
    
    if (keysToDelete.length > 0) {
      console.log(`[Cache] Cleanup removed ${keysToDelete.length} expired entries, freed ${freedSize} bytes`);
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    entryCount: number;
    sizeInBytes: number;
    sizeInMB: number;
    hitRate: number;
    missRate: number;
    evictionCount: number;
    keys: string[];
    maxSizeMB: number;
    maxEntries: number;
  } {
    const totalRequests = this.hitCount + this.missCount;
    const hitRate = totalRequests > 0 ? this.hitCount / totalRequests : 0;
    const missRate = totalRequests > 0 ? this.missCount / totalRequests : 0;
    
    return {
      entryCount: this.cache.size,
      sizeInBytes: this.currentCacheSize,
      sizeInMB: Number((this.currentCacheSize / (1024 * 1024)).toFixed(2)),
      hitRate: Number((hitRate * 100).toFixed(2)),
      missRate: Number((missRate * 100).toFixed(2)),
      evictionCount: this.evictionCount,
      keys: Array.from(this.cache.keys()),
      maxSizeMB: CacheService.CACHE_CONFIG.MAX_SIZE / (1024 * 1024),
      maxEntries: CacheService.CACHE_CONFIG.MAX_ENTRIES
    };
  }

  // Specific cache methods for common data types

  /**
   * Cache active employees
   */
  setActiveEmployees(employees: EmployeeDocument[]): void {
    this.set('employees:active', employees, CacheService.CACHE_DURATIONS.EMPLOYEES);
  }

  /**
   * Get cached active employees
   */
  getActiveEmployees(): EmployeeDocument[] | null {
    return this.get<EmployeeDocument[]>('employees:active');
  }

  /**
   * Cache customer data
   */
  setCustomer(customerId: string, customer: PayingClient): void {
    this.set(`customer:${customerId}`, customer, CacheService.CACHE_DURATIONS.CUSTOMERS);
  }

  /**
   * Get cached customer data
   */
  getCustomer(customerId: string): PayingClient | null {
    return this.get<PayingClient>(`customer:${customerId}`);
  }

  /**
   * Invalidate customer cache (useful when customer is updated)
   */
  invalidateCustomer(customerId: string): void {
    this.delete(`customer:${customerId}`);
  }

  /**
   * Invalidate all customer caches
   */
  invalidateAllCustomers(): void {
    const keysToDelete: string[] = [];
    let freedSize = 0;
    
    this.cache.forEach((entry, key) => {
      if (key.startsWith('customer:')) {
        keysToDelete.push(key);
        freedSize += entry.size;
      }
    });
    
    keysToDelete.forEach(key => {
      this.cache.delete(key);
    });
    
    this.currentCacheSize -= freedSize;
  }

  /**
   * Invalidate employee cache
   */
  invalidateEmployees(): void {
    this.delete('employees:active');
  }
}

export default CacheService;